import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtTime } from "@/lib/music";

export const RecordDialog = ({ open, onOpenChange, hasTracks, startAt, bpm, loop, loopLocked, onUpgrade, onArm, onDisarm, onDone }) => {
  const [click, setClick] = useState(false);
  const [countIn, setCountIn] = useState(true);
  const [leadLeft, setLeadLeft] = useState(0);
  const [recording, setRecording] = useState(false);
  const [level, setLevel] = useState(0);
  const [secs, setSecs] = useState(0);
  const [backing, setBacking] = useState(true);
  const rec = useRef({});

  const cleanup = () => {
    const r = rec.current;
    cancelAnimationFrame(r.raf);
    r.stream?.getTracks().forEach((t) => t.stop());
    r.ctx?.close();
    rec.current = {};
    setRecording(false);
    setLevel(0);
    setLeadLeft(0);
  };

  useEffect(() => () => cleanup(), []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false } });
      const ctx = new AudioContext();
      const an = ctx.createAnalyser();
      ctx.createMediaStreamSource(stream).connect(an);
      const data = new Uint8Array(an.fftSize);
      const mr = new MediaRecorder(stream);
      const inLatency = stream.getAudioTracks()[0]?.getSettings?.().latency || 0.01;
      const chunks = [];
      mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        cleanup();
        onDisarm();
        onDone(blob, inLatency);
      };
      const t0 = performance.now();
      const tick = () => {
        an.getByteTimeDomainData(data);
        let m = 0;
        data.forEach((v) => (m = Math.max(m, Math.abs(v - 128) / 128)));
        setLevel(m);
        const el = (performance.now() - t0) / 1000;
        setSecs(Math.max(0, el - rec.current.lead));
        setLeadLeft(Math.max(0, rec.current.lead - el));
        rec.current.raf = requestAnimationFrame(tick);
      };
      rec.current = { stream, ctx, mr, lead: 0 };
      mr.start();
      rec.current.lead = onArm({ backing: backing && hasTracks, countIn, click });
      setRecording(true);
      tick();
    } catch (e) {
      toast.error("Microphone access was denied");
    }
  };

  const close = (o) => {
    if (!o && recording) {
      rec.current.mr.onstop = null;
      rec.current.mr.stop();
      cleanup();
      onDisarm();
    }
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="record-modal" className="bg-[#141018] border-[#5a1e28] rounded-none">
        <DialogHeader>
          <DialogTitle className="font-gothic uppercase tracking-wider text-amber-100/90">Record a voice</DialogTitle>
          <DialogDescription className="font-cormorant italic text-base text-stone-400">{loop ? (
            <span data-testid="record-loop-info">
              Loop recording <span className="font-mono-vox not-italic text-amber-300">{fmtTime(loop.start)}–{fmtTime(loop.end)}</span> — every pass becomes its own take.
            </span>
          ) : (
            <span>
              Punch in from the playhead at <span className="font-mono-vox not-italic text-amber-300" data-testid="record-start-position">{fmtTime(startAt)}</span>.
            </span>
          )} Wear headphones to avoid bleed — timing is auto-compensated and can be fine-tuned with Sync offset.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-6 py-6">
          <button
            data-testid={recording ? "record-stop-btn" : "record-start-btn"}
            onClick={recording ? () => rec.current.mr.stop() : start}
            className={`w-24 h-24 rounded-full flex items-center justify-center border-2 transition-colors ${recording ? "bg-[#EF4444] border-[#EF4444] pulse-ring" : "bg-[#8B1E2B] border-[#c92a3e] hover:bg-[#a3243a]"}`}
          >
            {recording ? <Square size={28} fill="white" /> : <Mic size={32} />}
          </button>
          <p className="font-mono-vox text-lg tracking-widest text-amber-400" data-testid="record-timer">
            {leadLeft > 0 ? `Count-in ${Math.ceil(leadLeft / (60 / bpm))}` : `${secs.toFixed(1)}s`}
          </p>
          <div className="w-full h-2 bg-[#0f0d11] border border-[#352d40]">
            <div className="h-full bg-gradient-to-r from-[#D99B43] to-[#EF4444]" style={{ width: `${Math.min(100, level * 140)}%` }} />
          </div>
          <label className="flex items-center gap-2 text-sm text-stone-300">
            <Checkbox data-testid="record-countin-checkbox" checked={countIn} onCheckedChange={(v) => setCountIn(!!v)} disabled={recording} />
            Count-in: 4 clicks at {bpm} BPM
          </label>
          {loopLocked && (
            <p className="text-xs text-stone-400 border border-[#D99B43]/40 px-3 py-2" data-testid="record-loop-locked">
              Loop recording is a Pro feature. This take will punch in once from the playhead.{" "}
              <button onClick={onUpgrade} data-testid="record-loop-upgrade-btn" className="text-amber-300 underline">Upgrade</button>
            </p>
          )}
          <label className="flex items-center gap-2 text-sm text-stone-300">
            <Checkbox data-testid="record-metronome-checkbox" checked={click} onCheckedChange={(v) => setClick(!!v)} disabled={recording} />
            Metronome click throughout ({bpm} BPM, not recorded)
          </label>
          {hasTracks && (
            <label className="flex items-center gap-2 text-sm text-stone-300">
              <Checkbox data-testid="record-backing-checkbox" checked={backing} onCheckedChange={(v) => setBacking(!!v)} disabled={recording} />
              Play the song & existing voices while recording
            </label>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
